'use client';
import React from 'react';
import { Shield, Eye, CheckSquare, Database, Download, Lock, Sparkles } from 'lucide-react';

const trustPoints = [
  {
    icon: Shield,
    title: 'You stay in control.',
    desc: 'AgentOS makes recommendations. You make decisions. Nothing happens without your approval.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.08)',
    border: 'rgba(0,212,255,0.18)',
  },
  {
    icon: Eye,
    title: 'Every AI recommendation is explainable.',
    desc: 'We show you why AgentOS is suggesting something. No black boxes. No mystery.',
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.08)',
    border: 'rgba(167,139,250,0.18)',
  },
  {
    icon: CheckSquare,
    title: 'Approve messages before sending.',
    desc: 'AgentOS drafts. You review. You send. Your name is on every message — and you control every word.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.07)',
    border: 'rgba(0,212,255,0.15)',
  },
  {
    icon: Database,
    title: 'Your data belongs to you.',
    desc: 'We don\'t sell your data. We don\'t train on your clients. Your business information is yours — period.',
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.07)',
    border: 'rgba(34,197,94,0.15)',
  },
  {
    icon: Download,
    title: 'Export anytime.',
    desc: 'Download your contacts, conversations, and data whenever you want. No lock-in. No hostage data.',
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.07)',
    border: 'rgba(167,139,250,0.15)',
  },
  {
    icon: Lock,
    title: 'No hidden automation.',
    desc: 'AgentOS never acts on your behalf without your knowledge. Privacy first. Transparency always.',
    color: '#00D4FF',
    bg: 'rgba(0,212,255,0.06)',
    border: 'rgba(0,212,255,0.14)',
  },
];

export default function TestimonialsSection() {
  return (
    <section id="trust" className="py-24 relative overflow-hidden" style={{ background: '#0B0F19' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(0,212,255,0.03) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            Built on Trust
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            AI you can actually{' '}
            <span className="gradient-cyan-lavender-text">trust.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(240,244,255,0.55)' }}>
            We believe AI should work for you — not around you. Every decision we make starts with this principle.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {trustPoints.map((point) => (
            <div
              key={point.title}
              className="rounded-2xl p-7 card-hover cursor-default flex flex-col gap-4"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = point.bg;
                (e.currentTarget as HTMLElement).style.borderColor = point.border;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)';
              }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: point.bg, border: `1px solid ${point.border}` }}
              >
                <point.icon size={22} style={{ color: point.color }} />
              </div>
              <div>
                <h3 className="text-base font-bold mb-2" style={{ color: '#F0F4FF' }}>{point.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>{point.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust statement */}
        <div
          className="mt-14 rounded-3xl p-8 lg:p-12 text-center relative overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
          />
          <div className="relative">
            <p
              className="text-2xl sm:text-3xl font-extrabold leading-snug max-w-3xl mx-auto"
              style={{ color: '#F0F4FF' }}
            >
              "Trust isn't a feature. It's the foundation."
            </p>
            <p className="mt-4 text-base" style={{ color: 'rgba(240,244,255,0.50)' }}>
              AgentOS is designed to earn your trust every day — not just on the sign-up page.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
